/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
// import { saveAs } from "file-saver";
import Swal from 'sweetalert2'
import * as THREE from 'three'

import Render3DPlugin from './Render3DPlugin'
import { ARViewerWeb } from '.'

/** Uses the 8th wall library to provide the AR experience. Provides 6 degrees of freedom plus camera sync. */
export default class Plugin8thWall {
  ar: ARViewerWeb
  appKey: string
  poweredDiv: HTMLDivElement
  recordDiv: HTMLDivElement
  stopDiv: HTMLDivElement
  canvas: HTMLCanvasElement
  clock: any

  constructor(ar: ARViewerWeb) {
    // Store AR
    this.ar = ar
    this.appKey = ar.config.appKey8thWall

    this.poweredDiv = document.createElement('div')
    this.poweredDiv.style.cssText =
      'position: absolute; z-index: 10; bottom: 0px; left: 0px; width: 100%; padding: 20px 0px; font-size: 0.882rem; color: rgba(255, 255, 255, 0.75); font-style: italic; text-align: center; '
    this.poweredDiv.innerHTML = 'Powered by <b>8th wall</b>'

    this.recordDiv = document.createElement('div')
    this.recordDiv.style.cssText =
      'display: block; position: absolute; z-index: 15; top: 10px; right: 10px; width: 100px; padding: 10px 0px; text-align: center; border-radius: 10px; color: white; background-color: red; '
    this.recordDiv.innerHTML = 'RECORD'

    this.stopDiv = document.createElement('div')
    this.stopDiv.style.cssText =
      'display: none; position: absolute; z-index: 15; top: 10px; right: 10px; width: 100px; padding: 10px 0px; text-align: center; border-radius: 10px; color: white; background-color: red; '
    this.stopDiv.innerHTML = 'STOP'

    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText =
      'position: absolute; z-index: 2; top: 0px; left: 0px; width: 100%; height: 100%;'
  }

  get name() {
    return 'Plugin8thWall'
  }

  async load(div: HTMLDivElement) {
    // Show status
    this.ar.setState({ loadingText: 'Loading 8th Wall...' })

    // Create "Powered by" logo
    div.appendChild(this.poweredDiv)

    // if (this.ar.customSession?.config.showRecord) {
    // 	div.appendChild(this.recordDiv);
    // 	this.recordDiv.addEventListener("click", this.recordVideo.bind(this));
    // }

    div.appendChild(this.stopDiv)
    this.stopDiv.addEventListener('click', this.stopVideo.bind(this))

    // Create canvas element
    div.appendChild(this.canvas)

    // Add click handler to canvas
    this.canvas.addEventListener('mousedown', this.onClick.bind(this))
    this.canvas.addEventListener('touchstart', this.onClick.bind(this))

    // Create clock to measure delta between frames
    this.clock = new THREE.Clock()

    // Load the 8th wall code
    await loadScript('xrweb_script', '//apps.8thwall.com/xrweb?appKey=' + this.appKey)
    // await loadScript(
    // 	"xrextras_script",
    // 	"//cdn.8thwall.com/web/xrextras/xrextras.js"
    // );

    // Test to see if we can get the camera
    // const stream = await getEnvironmentCamera();
    // stream.getTracks().forEach(track => track.stop())

    // Wait until 8th wall is ready
    await waitUntil(
      (e: any) => {
        return typeof (window as any).XR8 !== 'undefined'
      },
      15000,
      'Unable to load the 8th wall library.'
    )

    // Setup 8th wall pipeline
    const XR8 = (window as any).XR8
    XR8.addCameraPipelineModules([
      XR8.XrController.pipelineModule(),
      XR8.GlTextureRenderer.pipelineModule(),
      XR8.Threejs.pipelineModule()
      // window.XRExtras.AlmostThere.pipelineModule(),
      // window.XRExtras.Loading.pipelineModule(),
      // window.XRExtras.RuntimeError.pipelineModule(),
      // window.XR8.MediaRecorder.pipelineModule(),
    ])
    console.info('8th wall pipeline loaded..')

    // Add our own pipeline module and start it
    await new Promise<void>((resolve, reject) => {
      let hasReturnedPromise = false
      XR8.addCameraPipelineModule({
        name: 'ar-app',
        onException: reject,
        onUpdate: (e: any) => {
          // Resolve promise on first frame
          if (!hasReturnedPromise) {
            resolve()
            hasReturnedPromise = true
          }

          // Pass event through
          this.XR8FrameUpdate()
        }
      })
      XR8.run({ canvas: this.canvas })
    })
    console.info('camera pipeline module loaded')
    // Get scene information, expose it to other plugins
    const { scene, camera, renderer } = XR8.Threejs.xrScene()
    this.ar.camera = camera
    // this.ar.camera.far = 100;
    this.ar.scene = scene
    this.ar.renderer = renderer

    // Prepare renderer for loading glTF models
    renderer.outputEncoding = THREE.sRGBEncoding
    // renderer.setPixelRatio(window.devicePixelRatio || 1)

    // const color = ARConfig.features?.ar.ambientLightColor
    // 	? Number(ARConfig.features?.ar.ambientLightColor)
    // 	: 0x404040;
    // const intensity = ARConfig.features?.ar.ambientLightIntensity
    // 	? ARConfig.features?.ar.ambientLightIntensity
    // 	: 1;
    // Setup ambient lighting
    // var light = new THREE.AmbientLight(color, intensity);
    // this.ar.scene.add(light);

    // Setup environment map
    this.ar.scene.environment = await Render3DPlugin.loadSkyboxCubeMap()

    // Setup audio listener
    this.ar.audioListener = new THREE.AudioListener()
    this.ar.camera.add(this.ar.audioListener)

    // Sync the xr controller's 6DoF position and camera paremeters with our scene.
    XR8.XrController.updateCameraProjectionMatrix({
      origin: camera.position,
      facing: camera.quaternion
    })

    // window.XR8.MediaRecorder.configure({
    // 	maxDurationMs: 15000,
    // });

    // Setup resize handler
    this.onResize = this.onResize.bind(this)
    window.addEventListener('resize', this.onResize)
    this.onResize()
    console.info('8th wall loaded')
  }

  /** @private Called when the view is resized */
  onResize() {
    // Update renderer size
    this.ar.renderer.setSize(window.innerWidth, window.innerHeight, false)
    this.ar.camera.aspect = window.innerWidth / window.innerHeight
    this.ar.camera.updateProjectionMatrix()
  }

  /** On load complete */
  loadComplete() {
    // Remove addition to loading screen
    this.poweredDiv.parentElement?.removeChild(this.poweredDiv)
  }

  stopVideo() {
    console.info('Stop Video')
    // window.XR8.MediaRecorder.stopRecording();
  }

  recordVideo() {
    ;(window as any).XR8.MediaRecorder.recordVideo({
      onVideoReady: async (result: any) => {
        this.recordDiv.style.display = 'block'
        this.stopDiv.style.display = 'none'
        Swal.close()
        try {
          const file = new File([result.videoBlob], 'video.mp4', {
            type: 'video/mp4'
          })
          const filesArray = [file]
          if (navigator.share && navigator.canShare?.({ files: filesArray })) {
            const response = await Swal.fire({
              title: 'Share Video',
              text: 'Click to share your video',
              showConfirmButton: true,
              showCancelButton: true
            })
            if (response.isConfirmed) {
              navigator.share({
                files: filesArray
              })
            }
          } else {
            // saveAs(result.videoBlob, "video.mp4");
            Swal.fire({
              icon: 'info',
              title: 'Saving Video',
              text: 'Video is saved in your downloads'
            })
          }
        } catch (err: any) {
          console.error(err.message)
        }
      },
      onStop: () => {
        Swal.fire({
          showConfirmButton: false,
          text: 'Please wait while we process your video...'
        })
        this.recordDiv.style.display = 'block'
        this.stopDiv.style.display = 'none'
      }
    })
    this.recordDiv.style.display = 'none'
    this.stopDiv.style.display = 'block'
  }

  /** Called every frame */
  XR8FrameUpdate() {
    // Get delta time
    const delta = Math.min(this.clock.getDelta(), 0.5)

    // Stop if still loading or something has gone wrong
    if (this.ar.state.isLoading || this.ar.state.error) return

    // Notify any interested plugins, stop if any of them want us to skip this frame
    let shouldSkip = false
    for (const plugin of this.ar.plugins) {
      if (plugin.onRender3D) {
        if (plugin.onRender3D(delta, this.ar.scene, this.ar.camera) === false) shouldSkip = true
      }
    }

    if (shouldSkip) return
  }

  async unload() {
    // Stop and clear 8th wall
    console.info('Unloading 8th Wall', !!(window as any).XR8)
    if ((window as any).XR8) {
      ;(window as any).XR8.stop()
      ;(window as any).XR8.clearCameraPipelineModules()
    }
    this.canvas?.parentNode?.removeChild(this.canvas)

    // Remove listeners
    window.removeEventListener('resize', this.onResize)
  }

  /** Called when the user clicks on the canvas */
  onClick(e: any) {
    e.preventDefault()

    // TODO: This code is the same as Render3D, reuse code instead of copying this function

    // Get position of touch on the page
    let x = e.changedTouches?.[0].pageX || e.pageX || 0
    let y = e.changedTouches?.[0].pageY || e.pageY || 0

    // Calculate position of touch within the canvas element
    const rect = this.canvas.getBoundingClientRect()
    x -= rect.left
    y -= rect.top

    // Normalize the coordinates from pixels to (-1 to 1)
    x = (x / rect.width) * 2 - 1
    y = (y / rect.height) * 2 - 1
    y *= -1

    // Raytrace to find the touched object
    const raycaster = new THREE.Raycaster()
    const mouse = new THREE.Vector2(x, y)
    raycaster.setFromCamera(mouse, this.ar.camera)
    const intersects = raycaster.intersectObject(this.ar.scene, true)
    console.log('touch', x, y, intersects)
    if (intersects.length === 0) return

    // Walk up the chain until we find an object with an .onClick handler
    let obj: any = intersects[0].object
    while (obj) {
      // Check for handler
      if (obj.onClick) {
        // Call it
        obj.onClick(e)
        break
      } else {
        // No handler, keep walking
        obj = obj.parent
      }
    }
  }
}

// Load remote script, return promise when complete
function loadScript(id: string, src: string) {
  // Create promise
  return new Promise<void>((resolve, reject) => {
    let s: any = document.getElementById(id)
    if (!s) {
      // Insert tag
      s = document.createElement('script')
      s.id = id
      s.type = 'text/javascript'
      s.src = src
      document.body.appendChild(s)

      // Add load and error handlers
      s.onload = () => resolve()
      s.onerror = () => reject(new Error('Unable to load script.'))
    } else {
      resolve()
    }
  })
}

// Wait until a test function returns true, or until a timeout occurs
function waitUntil(testFunction: any, timeout: number, timeoutErrorText: string) {
  // Create promise
  return new Promise<void>((resolve, reject) => {
    // Create timer
    const startTime = Date.now()
    const timer = setInterval((e: any) => {
      // Catch errors
      try {
        // Check if time is up
        const timeCheck = Date.now() - startTime
        if (timeCheck > timeout) throw new Error(timeoutErrorText)

        // Check if passed
        if (!testFunction()) return

        // We're done!
        clearInterval(timer)
        resolve()
      } catch (err) {
        clearInterval(timer)
        reject(err)
      }
    }, 250)
  })
}
